import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { orderBy } from "lodash";

import useGetChatBotMessages from "@fartherfinance/frontend/api/Chatbot/hooks/useGetChatBotMessages";
import { ChatBotMessage } from "@fartherfinance/frontend/api/Chatbot/Types";

import { useGetIsValidSessionid } from "../../hooks/useGetIsValidSessionid";
import { useChatStore } from "../../reducer/Context";
import ChatMessage from "../ChatMessage/ChatMessage";
import LoadingDots from "../LoadingDots";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

import styles from "../../style/ChatBotWindow.module.css";

interface MessageListProps {
  isLoading: boolean;
  isExpanded: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ isLoading, isExpanded }) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const chatRef = useRef<HTMLDivElement>(null);

  const { sessionId, optimisticUserMessage } = useChatStore();

  const advisorAuth = useAdvisorRequestAuth();

  const isValidSessionId = useGetIsValidSessionid(sessionId);

  const { data: chatBotMessagesResponse } = useGetChatBotMessages(
    sessionId,
    isValidSessionId ? advisorAuth : null
  );

  const messages = useMemo(() => {
    if (!chatBotMessagesResponse) {
      return [];
    }
    return chatBotMessagesResponse.data;
  }, [chatBotMessagesResponse]);

  // Check if scrollbar is needed and add appropriate class
  const checkScrollbar = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    // Check if content is taller than container
    const hasScroll = container.scrollHeight > container.clientHeight;

    // Add or remove class based on scroll status
    if (hasScroll) {
      container.classList.add(styles.hasScroll);
    } else {
      container.classList.remove(styles.hasScroll);
    }
  }, []);

  // Auto-scroll to the bottom when messages change or when expanded state changes
  useEffect(() => {
    chatRef.current?.scrollIntoView({ behavior: "smooth" });
    // Check if scrollbar is needed after messages update or expanded state change
    checkScrollbar();
  }, [messages, isLoading, isExpanded, checkScrollbar]);

  // Add event listeners to handle scrollbar visibility
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    // Check scrollbar initially
    checkScrollbar();

    // Add resize listener to recheck when window size changes
    const handleResize = () => {
      checkScrollbar();
    };

    // Set up scroll event listener for auto-hiding scrollbar
    const handleScroll = () => {
      // When scrolling, ensure the scrollbar is visible
      container.classList.add(styles.scrolling);

      // Clear previous timeout
      if (container.scrollTimer) {
        clearTimeout(container.scrollTimer);
      }

      // Set timeout to hide scrollbar after scrolling stops
      container.scrollTimer = setTimeout(() => {
        container.classList.remove(styles.scrolling);
      }, 1500); // Hide after 1.5 seconds of inactivity
    };

    // Register event listeners
    window.addEventListener("resize", handleResize);
    container.addEventListener("scroll", handleScroll);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      container.removeEventListener("scroll", handleScroll);
      if (container.scrollTimer) {
        clearTimeout(container.scrollTimer);
      }
    };
  }, [checkScrollbar]);

  // Add a small animation delay for smooth transition when expanding state changes
  useEffect(() => {
    if (isExpanded) {
      // Check scrollbar sizing after expansion is complete
      setTimeout(() => {
        checkScrollbar();
      }, 450); // Wait slightly longer than the transition duration
    }
  }, [isExpanded, checkScrollbar]);

  const optimisticMessages = useMemo(() => {
    const allMessages = optimisticUserMessage
      ? [...messages, optimisticUserMessage]
      : messages;

    return orderBy(
      allMessages.filter((el): el is ChatBotMessage => el !== null),
      (msg) => new Date(msg.timestamp),
      "asc"
    );
  }, [messages, optimisticUserMessage]);

  return (
    <div className={styles.messagesContainer} ref={messagesContainerRef}>
      {optimisticMessages.map((message) => (
        <ChatMessage key={message.messageId} message={message} />
      ))}

      {isLoading && (
        <div className={styles.loading}>
          <LoadingDots />
        </div>
      )}

      <div id="chat-bottom" style={{ height: 0 }} ref={chatRef} />
    </div>
  );
};

export default MessageList;
